<script setup lang="ts">
import { computed, ref } from "vue";
import { useDeviceState } from "@/stores/DeviceState";
import { useDates } from "@/composable/useDates";
import { useUnsyncedDrawings } from "@/composable/useUnsyncedDrawings";
import { OFFLINE_BANNER_DELAY_MS } from "@/utils/constants";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();
const deviceState = useDeviceState();
const { unsyncedCount } = useUnsyncedDrawings();
const { formatDistanceToNow } = useDates();

function getTodayDateString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(now.getDate()).padStart(2, "0")}`;
}

function isDismissedToday(): boolean {
  const dismissedDate = $ls.getItem(LocalStorageKey.OFFLINE_BANNER_DISMISSED);
  return dismissedDate === getTodayDateString();
}

const isDismissed = ref(isDismissedToday());

const offlineDuration = computed(() => {
  if (!deviceState.offlineSince) return "";
  return formatDistanceToNow(deviceState.offlineSince, false);
});

const isOfflineLongEnough = computed(() => {
  if (!deviceState.offlineSince) return false;
  const now = new Date().getTime();
  const offlineTime = new Date(deviceState.offlineSince).getTime();
  return now - offlineTime >= OFFLINE_BANNER_DELAY_MS;
});

const showBanner = computed(() => {
  return (
    deviceState.isOnline === false &&
    isOfflineLongEnough.value &&
    unsyncedCount.value > 0 &&
    !isDismissed.value
  );
});

function dismiss() {
  isDismissed.value = true;
  $ls.setItem(LocalStorageKey.OFFLINE_BANNER_DISMISSED, getTodayDateString());
}
</script>

<template>
  <div v-if="showBanner" class="offline-banner">
    <div class="banner-content">
      <div class="banner-icon">
        <i class="bi bi-wifi-off"></i>
      </div>
      <div class="banner-text">
        <strong>{{
          t("dashboard:sync.page.offlineBanner.title", {
            duration: offlineDuration,
          })
        }}</strong>
        <span>{{
          t("dashboard:sync.page.offlineBanner.message", {
            count: unsyncedCount,
          })
        }}</span>
      </div>
    </div>
    <button class="banner-close" @click="dismiss" aria-label="Close">
      <i class="bi bi-x-lg"></i>
    </button>
  </div>
</template>

<style scoped>
.offline-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: var(--bs-warning-bg-subtle);
  border-bottom: 2px solid var(--bs-warning);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: var(--bs-warning-border-subtle);
  color: var(--bs-warning-text-emphasis);
  flex-shrink: 0;
}

.banner-icon i {
  font-size: 1.2rem;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.banner-text strong {
  color: var(--bs-warning-text-emphasis);
  font-size: 0.9rem;
}

.banner-text span {
  color: var(--bs-body-color);
  font-size: 0.8rem;
  opacity: 0.85;
}

.banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: var(--bs-warning-text-emphasis);
  opacity: 0.7;
  transition: opacity 0.2s;
  flex-shrink: 0;
}

.banner-close:hover {
  opacity: 1;
}

/* Responsive */
@media (max-width: 480px) {
  .offline-banner {
    padding: 0.5rem 0.75rem;
  }

  .banner-icon {
    width: 2rem;
    height: 2rem;
  }

  .banner-icon i {
    font-size: 1rem;
  }

  .banner-text strong {
    font-size: 0.85rem;
  }

  .banner-text span {
    font-size: 0.75rem;
  }
}
</style>
